import { AppCheckbox } from 'shared/UI/UIKit/Checkbox';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import {
  educationParallelTextRepresentation,
  SCHOOL_PARALLEL_GROUPS,
} from 'entities/education-class';
import type { EducationClass, Parallel } from 'services/api';
import { sortEducationClassesBySchoolOrder } from '../../../model/util';

type EducationClassesCheckboxGroupsProps = {
  educationClasses: EducationClass[];
  value: number[];
  onChange: (educationalClassIds: number[]) => void;
  disabled?: boolean;
};

export const EducationClassesCheckboxGroups = ({
  educationClasses,
  value,
  onChange,
  disabled = false,
}: EducationClassesCheckboxGroupsProps) => {
  const allIds = educationClasses.map((c) => c.id);
  const allChecked = allIds.length > 0 && allIds.every((id) => value.includes(id));
  const allIndeterminate = value.length > 0 && !allChecked && value.length < allIds.length;

  const getGroup = (parallels: readonly Parallel[]) =>
    educationClasses.filter((c) => parallels.includes(c.parallel));

  const [primaryGroup, middleGroup, highGroup] = SCHOOL_PARALLEL_GROUPS;
  const primaryClasses = sortEducationClassesBySchoolOrder(getGroup(primaryGroup.parallels));
  const middleClasses = sortEducationClassesBySchoolOrder(getGroup(middleGroup.parallels));
  const highClasses = sortEducationClassesBySchoolOrder(getGroup(highGroup.parallels));

  const primaryIds = primaryClasses.map((c) => c.id);
  const middleIds = middleClasses.map((c) => c.id);
  const highIds = highClasses.map((c) => c.id);

  const isGroupChecked = (ids: number[]) => ids.length > 0 && ids.every((id) => value.includes(id));

  const isGroupIndeterminate = (ids: number[]) => {
    const someSelected = ids.some((id) => value.includes(id));
    return someSelected && !isGroupChecked(ids);
  };

  const handleCheckAll = (checked: boolean) => {
    onChange(checked ? [...allIds] : []);
  };

  const handleGroupChange = (ids: number[], checked: boolean) => {
    if (!ids.length) {
      return;
    }
    if (checked) {
      onChange(Array.from(new Set([...value, ...ids])));
    } else {
      onChange(value.filter((id) => !ids.includes(id)));
    }
  };

  const handleClassChange = (id: number, checked: boolean) => {
    if (checked) {
      if (!value.includes(id)) {
        onChange([...value, id]);
      }
    } else {
      onChange(value.filter((k) => k !== id));
    }
  };

  return (
    <AppFlex direction="column" unit="12">
      <div>
        <AppCheckbox
          disabled={disabled}
          checked={allChecked}
          indeterminate={allIndeterminate}
          onChange={(e) => handleCheckAll(e.target.checked)}
        >
          Выбрать все классы
        </AppCheckbox>
      </div>

      <AppFlex direction="column" unit="8">
        <AppCheckbox
          disabled={disabled}
          checked={isGroupChecked(primaryIds)}
          indeterminate={isGroupIndeterminate(primaryIds)}
          onChange={(e) => handleGroupChange(primaryIds, e.target.checked)}
        >
          {primaryGroup.label}
        </AppCheckbox>
        <AppFlex direction="column" unit="4" style={{ marginLeft: 24 }}>
          {primaryClasses.map((cls) => {
            const label = `${educationParallelTextRepresentation[cls.parallel]}${cls.litera} класс`;
            return (
              <AppCheckbox
                key={cls.id}
                disabled={disabled}
                checked={value.includes(cls.id)}
                onChange={(e) => handleClassChange(cls.id, e.target.checked)}
              >
                {label}
              </AppCheckbox>
            );
          })}
        </AppFlex>

        <AppCheckbox
          disabled={disabled}
          checked={isGroupChecked(middleIds)}
          indeterminate={isGroupIndeterminate(middleIds)}
          onChange={(e) => handleGroupChange(middleIds, e.target.checked)}
        >
          {middleGroup.label}
        </AppCheckbox>
        <AppFlex direction="column" unit="4" style={{ marginLeft: 24 }}>
          {middleClasses.map((cls) => {
            const label = `${educationParallelTextRepresentation[cls.parallel]}${cls.litera} класс`;
            return (
              <AppCheckbox
                key={cls.id}
                disabled={disabled}
                checked={value.includes(cls.id)}
                onChange={(e) => handleClassChange(cls.id, e.target.checked)}
              >
                {label}
              </AppCheckbox>
            );
          })}
        </AppFlex>

        <AppCheckbox
          disabled={disabled}
          checked={isGroupChecked(highIds)}
          indeterminate={isGroupIndeterminate(highIds)}
          onChange={(e) => handleGroupChange(highIds, e.target.checked)}
        >
          {highGroup.label}
        </AppCheckbox>
        <AppFlex direction="column" unit="4" style={{ marginLeft: 24 }}>
          {highClasses.map((cls) => {
            const label = `${educationParallelTextRepresentation[cls.parallel]}${cls.litera} класс`;
            return (
              <AppCheckbox
                key={cls.id}
                disabled={disabled}
                checked={value.includes(cls.id)}
                onChange={(e) => handleClassChange(cls.id, e.target.checked)}
              >
                {label}
              </AppCheckbox>
            );
          })}
        </AppFlex>
      </AppFlex>
    </AppFlex>
  );
};
